export const en = {
  locale: 'en',
  nav: {
    setup: 'Control Center',
    news: 'News',
    brand: 'WC26 Sim',
    teamName: 'Team',
    tactics: 'Tactics',
    fixtures: 'Fixtures',
    standings: 'Standings',
    knockout: 'Knockout',
    teamStats: 'Team Stats',
    playerStats: 'Player Stats',
  },
  landing: {
    title: 'World Cup Simulation',
    subtitle: 'From the opening match to the final night. Choose your seat as a spectator or take the helm as a coach and push through a 48-team World Cup.',
    audienceMode: {
      title: 'Audience Mode',
      subtitle: 'Full Tournament View',
      desc: 'Step through every match of the tournament, tracking news, standings, knockout brackets, team stats, and leaderboards like a data center watching the entire World Cup.',
    },
    coachMode: {
      title: 'Coach Mode',
      subtitle: 'Take Charge of a National Team',
      desc: 'Select a national team and step into the coach\'s seat. Review your squad and player data, adjust formation, mentality, pressing, passing, and attack focus before each match, then use Continue to advance.',
    },
    completed: 'Completed',
    upcoming: 'Upcoming',
    championRanking: 'Champion Simulation',
    coachPickRanking: 'Coach Picks',
    noCompleted: 'No completed matches yet. Scores and closeness will appear after the simulation starts.',
    noUpcoming: '',
    noCoachPick: 'No Coach Mode selections recorded yet.',
    noChampion: 'No championship simulation records yet.',
    coachSetup: {
      title: 'Choose Your Team and Lead Them to Glory',
      back: 'Back',
      start: 'Start My World Cup Journey',
      processing: 'Creating Journey',
      hiddenTeamsOpen: "Open Hidden Teams",
      hiddenTeamsClose: "Close Hidden Teams",
    },
    rosterInfo: 'Group {group} · {count} Players',
    simKicker: {
      audience: 'Audience Mode · Full Coverage',
      coach: 'Coach Mode · Group {group}',
    },
    simTitle: {
      audience: 'World Cup Full Coverage Center',
      coach: '{flag} {name}\'s World Cup Journey',
    },
    warning: {
      leave: 'Leaving will lose the current simulation data.',
      confirm: 'Leaving will lose the current simulation data. Are you sure you want to leave?',
    },
    groupLabel: 'Group {id}',
    championCount: '{count} times',
    coachPickCount: '{count} picks',
  },
  common: {
    continue: 'Continue',
    processing: 'Processing',
    completed: 'Completed',
    close: 'Close',
    back: 'Back',
    vs: 'VS',
    restart: 'New Run',
    restartConfirm: 'Start a new run? Current simulation progress and locally saved data will be cleared.',
    requestFailed: 'Request failed',
    playAgain: 'Play Again',
  },
  team: {
    tab: {
      players: 'Players',
      fixtures: 'Fixtures',
      tactic: 'Tactics',
    },
    playerTable: {
      player: 'Player',
      club: 'Club',
      status: 'Status',
      position: 'Pos',
      ability: 'Rating',
      age: 'Age',
      caps: 'Caps',
      ntGoals: 'NT G',
      apps: 'App',
      goals: 'G',
      assists: 'A',
      rating: 'Avg',
    },
    status: {
      redCard: 'Red Card',
    },
    noTactic: 'This team has no completed matches yet, no previous tactic available.',
    tactic: {
      formation: 'Formation',
      mentality: 'Mentality',
      width: 'Width',
      tempo: 'Tempo',
      passing: 'Passing',
      attackRoute: 'Attack Focus',
      transition: 'Transition',
      pressing: 'Pressing',
      defensiveLine: 'Defensive Line',
      possession: 'Possession',
    },
  },
  tactics: {
    formation: 'Formation',
    more: 'More Formations',
    roster: {
      title: 'Squad',
      order: '#',
      status: 'Status',
      name: 'Name',
      position: 'Pos',
      ability: 'Rating',
      apps: 'App',
      goals: 'G',
      assists: 'A',
    },
    emptySlot: 'Empty',
    noData: 'No tactic data available',
    benchPrefix: 'Sub',
    starterPrefix: 'XI',
  },
  stats: {
    team: {
      title: 'Team Stats',
      subtitle: 'Ranked by cumulative performance',
      noData: 'No team data yet.',
      ranking: 'Team Rankings',
      value: 'Value',
      matches: 'Matches',
    },
    player: {
      title: 'Player Stats',
      subtitle: 'Player Rankings',
      noData: 'No player data yet.',
      nationality: 'Country',
      value: 'Value',
      apps: 'Apps',
    },
    playerTotals: {
      title: 'Squad Player Totals',
      subtitle: 'Completed matches only.',
      noData: 'Your team has not played any matches yet.',
    },
    groups: {
      overall: 'Overview',
      form: 'Form',
      discipline: 'Discipline',
      attack: 'Attack',
      creative: 'Creative',
      goalkeeping: 'Goalkeeping',
    },
    items: {
      avgPossession: 'Avg Possession',
      goalsFor: 'Goals For',
      xg: 'xG',
      shots: 'Shots',
      formText: 'Form',
      winStreak: 'Win Streak',
      unbeatenStreak: 'Unbeaten',
      winlessStreak: 'Winless',
      scorelessStreak: 'Scoreless',
      yellowCards: 'Yellow Cards',
      redCards: 'Red Cards',
      fouls: 'Fouls',
      goals: 'Goals',
      assists: 'Assists',
      minutesPerGoal: 'Min/Goal',
      apps: 'Apps',
      starts: 'Starts',
      averageRating: 'Avg Rating',
      keyPasses: 'Key Passes',
      passesAttempted: 'Passes',
      passCompletion: 'Pass %',
      saves: 'Saves',
    },
    descriptions: {
      avgPossession: 'Average possession in completed matches',
      goalsFor: 'Total goals scored',
      xg: 'Total xG accumulated',
      shots: 'Total shots taken',
      formText: 'Recent match form',
      winStreak: 'Current consecutive wins',
      unbeatenStreak: 'Current unbeaten run',
      winlessStreak: 'Current winless run',
      scorelessStreak: 'Current scoreless matches',
      yellowCards: 'Total yellow cards',
      redCards: 'Total red cards',
      fouls: 'Total fouls committed',
      playerGoals: 'Player goals leaderboard',
      playerAssists: 'Player assists leaderboard',
      playerXg: 'Player xG leaderboard',
      playerShots: 'Player shots leaderboard',
      minutesPerGoal: 'Minutes per goal (scorers only)',
      playerApps: 'Player appearances leaderboard',
      playerStarts: 'Matches with 45+ minutes',
      playerYellowCards: 'Player yellow cards',
      playerRedCards: 'Player red cards',
      playerAvgRating: 'Average rating (rated appearances)',
      playerKeyPasses: 'Player key passes',
      playerPasses: 'Total passes attempted',
      playerPassCompletion: 'Pass completion percentage',
      playerSaves: 'Goalkeeper saves leaderboard',
    },
  },
  position: {
    gk: 'Goalkeepers',
    def: 'Defenders',
    mid: 'Midfielders',
    fwd: 'Forwards',
  },
  stage: {
    'Group Stage': 'Group Stage',
    'Round of 32': 'Round of 32',
    'Round of 16': 'Round of 16',
    'Quarter-final': 'Quarter-final',
    'Semi-final': 'Semi-final',
    'Third Place': 'Third Place',
    'Final': 'Final',
  },
  match: {
    penaltyMarker: 'P',
    extraTimeMarker: 'AET',
    penalty: {
      scored: 'Scored',
      missed: 'Missed',
    },
    status: {
      finished: 'Full Time',
      notStarted: 'Not Started',
      pending: 'TBD',
    },
    viewDetail: 'View Match Details',
    modal: {
      teamData: 'Team Stats',
      playerData: 'Player Stats',
      finalResult: 'Final Result',
      restart: 'Play Again',
      subtitle: {
        extra: 'FT {normal}, AET {extra}.',
        penalty: 'FT {normal}, AET {extra}, Pens {penalty}, {winner} advance.',
      },
    },
    shootout: 'Penalty Shootout',
    extraTime: 'Extra Time',
    decision: {
      penalty: ' · Pens {home}-{away}',
      extra: ' · AET {home}-{away}',
    },
    news: {
      penalty: 'The match ended {normal} after regular time and {extra} after extra time; {winner} won {penalty} on penalties.',
      extraTime: 'The match ended {normal} after regular time, with {goals} more goals in extra time. Final score: {final}.',
    },
    fallback: '{home} and {away} finished {score} in the {stage}.',
    eventsFallback: 'The match was decided by midfield control and defensive details.',
    roundLabel: 'Round {n}',
    loading: {
      title: 'Simulating Matchday',
      preparing: 'Preparing {home} vs {away}',
      finished: 'Full Time {home} {score} {away}',
      noMatches: 'No pending matches on this matchday.',
    },
    xg: 'xG {home}/{away}',
    possession: 'Possession {home}/{away}',
    shots: 'Shots {home}/{away}',
    best: 'Best {player}',
    simulate: 'Simulate',
    simTitle: 'Simulation Results',
    simProgress: 'Running 10 simulations for this match',
    simulating: 'Simulating…',
  },
  fixture: {
    title: 'Fixtures',
    noDate: 'No Match Day',
    prevDay: 'Previous match day',
    nextDay: 'Next match day',
  },
  knockout: {
    title: 'Knockout',
    subtitle: 'The road from the Round of 32 to the Final',
    tabs: {
      bracket: 'Bracket',
    },
    stageNotGenerated: 'This stage schedule has not been generated yet.',
    tbd: 'TBD',
  },
  standings: {
    rank: 'Rank',
    team: 'Team',
    played: 'Pld',
    wins: 'W',
    draws: 'D',
    losses: 'L',
    goalsFor: 'GF',
    goalsAgainst: 'GA',
    goalDiff: 'GD',
    points: 'Pts',
    filter: 'Filter',
  },
  tacticsControls: {
    mentality: {
      label: 'Mentality',
      options: ['All-Out Attack', 'Attack', 'Positive', 'Balanced', 'Cautious', 'Defensive', 'Park the Bus'],
    },
    width: {
      label: 'Width',
      options: ['Narrow', 'Fairly Narrow', 'Standard', 'Fairly Wide', 'Wide'],
    },
    tempo: {
      label: 'Tempo',
      options: ['Slow', 'Fairly Slow', 'Normal', 'Fairly Fast', 'Fast'],
    },
    passing: {
      label: 'Passing Focus',
      options: ['Long Ball', 'Direct', 'Mixed', 'Short', 'Very Short'],
    },
    attackRoute: {
      label: 'Attack Focus',
      options: ['Wide', 'Mixed', 'Central'],
    },
    transition: {
      label: 'Transition',
      options: ['Play Out of Defence', 'Build from Back', 'Mixed', 'Quick Midfield', 'Quick Forward'],
    },
    defensiveLine: {
      label: 'Defensive Line',
      options: ['Low', 'Standard', 'High'],
    },
    pressing: {
      label: 'Pressing',
      options: ['Low', 'Mid', 'High'],
    },
    possession: {
      label: 'Possession',
      options: ['Sit Back', 'Less Possession', 'Balanced', 'More Possession', 'Total Control'],
    },
  },
  formations: {
    '4-2-3-1A': '4 defenders, 2 DMs, 2 wingers, 1 AM, 1 striker',
    '4-2-3-1S': '4 defenders, 2 DMs, 1 CM, 2 wingers, 1 striker',
    '4-3-3': '4 defenders, 3 midfielders, 3 forwards',
    '4-1-4-1': '4 defenders, 1 DM, flat 4 midfield, 1 striker',
    '4-1-2-2-1': '4 defenders, 1 DM, 2 CMs, 2 wingers, 1 striker',
    '4-3-1-2M': '4 defenders, 3 CMs, 1 AM, 2 forwards',
    '4-3-1-2D': '4 defenders, 3 DMs, 1 CM, 2 forwards',
    '4-3-2-1M': '4 defenders, 3 CMs, 2 AMs, 1 striker',
    '4-3-2-1D': '4 defenders, 3 DMs, 2 CMs, 1 striker',
    '4-3-2-1S': '4 defenders, 3 CMs, 2 wingers, 1 striker',
    '4-4-1-1': '4 defenders, flat 4 midfield, 1 AM, 1 striker',
    '4-2-2-2': '4 defenders, 2 DMs, 2 wingers, 2 forwards',
    '4-4-2F': '4 defenders, flat 4 midfield, 2 forwards',
    '4-4-2D': '4 defenders, diamond midfield, 2 forwards',
    '3-3-2-2': '3 CBs, 2 wingbacks, 1 DM, 2 CMs, 2 forwards',
    '3-2-4-1': '3 CBs, 2 DMs, 2 wide, 2 CMs, 1 striker',
    '3-2-2-2-1': '3 CBs, 2 wingbacks, 2 CMs, 2 wingers, 1 striker',
    '3-2-3-2D': '3 CBs, 2 DMs, 2 wide, 1 CM, 2 forwards',
    '3-2-3-2W': '3 CBs, 2 wingbacks, 3 CMs, 2 forwards',
    '3-4-1-2': '3 CBs, 2 wingbacks, 2 DMs, 1 AM, 2 strikers',
    '3-4-2-1S': '3 CBs, 2 wingbacks, 2 DMs, 2 wingers, 1 striker',
    '3-4-2-1C': '3 CBs, 2 wingbacks, 2 DMs, 2 AMs, 1 striker',
    '3-4-3F': '3 CBs, flat 4 midfield, 3 forwards',
    '3-4-3D': '3 CBs, diamond midfield, 3 forwards',
    '5-3-2': '5 defenders, 3 CMs, 2 forwards',
    '5-4-1F': '5 defenders, flat 4 midfield, 1 striker',
    '5-4-1D': '5 defenders, diamond midfield, 1 striker',
    '5-2-2-1': '5 defenders, 2 DMs, 2 wingers, 1 forward',
  },
  news: {
    title: 'News',
    subtitle: 'Matchday updates and key results',
    titleTexts: {
      groupRecap: 'Group Stage Review: Qualifiers, Surprises and Leaderboards',
      knockoutRecap: '{stage} Review: Qualifiers and Key Turning Points',
      stagePreview: '{stage} Preview: Key Matchups and Predictions',
      championRoad: '{flag} {name}\'s Road to Glory',
      awards: 'World Cup Awards: Best Player, Top Scorer and Best Keeper',
      review: 'World Cup Review: Champion, Upsets, Big Scores and Heroes',
      coachSummary: '{team}\'s Campaign Review',
    },
    kicker: {
      author: 'From the Author',
      guide: 'Quick Guide',
      welcome: {
        coach: 'Welcome to the Dugout',
        audience: 'Welcome to the Tournament Center',
      },
      stageRecap: 'Stage Recap · {stage}',
      stagePreview: 'Stage Preview · {stage}',
      champion: 'Champion Story',
      awards: 'Awards',
      tournamentReview: 'Tournament Review',
      pregame: 'Pre-Match',
      coachSummary: 'Campaign Summary',
    },
    default: {
      title: 'World Cup Simulation Not Started',
      body: 'Once you select a mode, the news center will begin updating.',
    },
    guide: {
      title: 'Continue will first take you through news, then advance the matchday',
      body: 'The {Continue} button is in the top-right corner of the page. In Audience Mode, unread news is shown first. After reading, you will see the day\'s fixtures. Click {Continue} again to simulate the day\'s matches.',
    },
    author: {
      title: 'A (Big) Note of Thanks from the Author',
      body: 'Thank you for enduring a programmer\'s sleep-deprived aesthetic and clicking here. If the simulation made you happy, give the author some praise; if the result was an upset… well, that\'s the beauty of football! For any feedback, reach me at x.com/diysimon or Xiaohongshu (diysimon).',
    },
    finalThanks: {
      title: 'Tournament Complete — Thank You for Playing!',
      body: 'The 2026 World Cup has come to an end. Thank you for experiencing this simulation! Follow the author (X @diysimon / Xiaohongshu diysimon) for updates, feedback, and future projects. Feel free to start a new run and try a different path!',
    },
    coachSummary: {
      intro: 'Your journey with {team} has come to an end. The team finished as {position}.',
      record: 'Across the tournament, {team} played {wins} wins, {draws} draws, {losses} losses, scoring {goalsFor} goals and conceding {goalsAgainst}.',
      position: '{team} finished in {position} place.',
      exit: 'They were eliminated in the {stage} by {opponent}.',
      exitNone: 'They finished the tournament undefeated in knockout play.',
      topScorer: 'Top scorer: {name} with {goals} goal(s).',
      noScorer: 'No player stood out in the scoring charts.',
      bestWin: 'Biggest win: {match}.',
      meta: '{wins}W {draws}D {losses}L | {goalsFor}GF {goalsAgainst}GA | {position}',
      factRecord: 'Record: {wins}W {draws}D {losses}L',
      factGoals: 'Goals: {goalsFor} For / {goalsAgainst} Against (diff {diff})',
      factTopScorer: 'Top scorer: {name} ({goals} goals)',
      factChampion: '{team} are World Cup Champions!',
      factFinish: 'Final position: {position}',
      positions: {
        champion: 'Champion',
        runnerUp: 'Runner-up',
        semiFinal: 'Semi-finalist',
        quarterFinal: 'Quarter-finalist',
        round16: 'Round of 16',
        round32: 'Round of 32',
        groupStage: 'Group Stage',
      },
    },
    welcome: {
      coach: {
        title: '{flag} {name} Begin Their World Cup Journey',
        body: 'You have taken charge of {name}. Use the left menu to view your squad, tactics, fixtures, standings, and stats. Click Continue in the top right to advance.',
      },
      audience: {
        title: '2026 World Cup Full Simulation Loaded',
        body: 'You are watching the tournament as a spectator. The system will progress match by match, continuously updating news, fixtures, standings, knockout brackets, and stats.',
      },
    },
    facts: {
      thanks: 'Thanks for playing',
      feedback: 'Feedback welcome',
      entertainment: 'Simulation for entertainment only',
      newsFirst: 'Newest news shown first',
      fixtureBrowse: 'Fixtures grouped by matchday',
      resultsGenerate: 'Match results generate more news',
      newsUpdates: 'News updates as matches progress',
      timeline: 'Timeline shows upcoming fixtures',
      refreshWarning: 'Refreshing or leaving loses current simulation',
      operation: 'How to play',
      preMatch: 'Pre-match briefing',
    },
    narrative: {
      intro: {
        penalty: '{winner} prevailed {score} on penalties after a tense {stage} battle.',
        extraTime: '{winner} won {winnerScore} after extra time to claim a hard-fought {stage} victory.',
        win: [
          '{winner} controlled the tempo in the {stage} and secured a {score} win.',
          '{winner} edged past their opponent with solid two-way play, finishing {score} in the {stage}.',
          '{winner} seized the key moments in this {stage} contest, ending {score}.',
          '{winner} made no mistake in the {stage}, closing out a {score} victory.',
          'The {stage} was tightly contested from the start, but {winner}\'s decisive goal made it {score}.',
        ],
        draw: [
          'A tense {stage} ended all square as {home} and {away} finished {score}.',
          '{home} and {away} traded blows in the {stage}, but neither could find a winner — {score} the final.',
          'The {stage} ended without a winner: {home} and {away} shared the spoils at {score}.',
        ],
      },
      noEvent: [
        'Neither side created a decisive breakthrough. The match was defined by midfield battles and blocks in the final third.',
        'Both defenses held their shape well, leaving few genuine goal-scoring opportunities.',
        'Finishing was the story of the match — promising moves kept breaking down at the last moment.',
      ],
      goal: {
        extraTime: [
          '{minute}, extra time reached its critical phase. {scorer} met {assister}\'s pass and {method} home to make it {score}.',
          '{minute}, {team} seized their extra-time chance as {scorer} {method} past the keeper, {team} {state}.',
          '{minute}, with legs tiring and space opening up, {scorer} calmly {method} to put the ball in the net, making it {score}.',
          '{minute}, {scorer} delivered the decisive extra-time moment, {method} to put {team} back in control.',
        ],
        withAssist: [
          '{minute}, {scorer} got on the end of {assister}\'s cross from the right, {method} ahead of the defender to make it {score}, {team} {state}.',
          '{minute}, {assister} slipped a key pass through, {scorer} latched onto it and {method} home, {team} {state}.',
          '{minute}, {team} worked a slick combination. {assister} laid it off for {scorer}, who {method} to make it {score}.',
          '{minute}, {scorer} profited from {assister}\'s delivery, {method} past the helpless keeper, {team} {state}.',
          '{minute}, {assister} played a line-breaking pass, {scorer} took it in stride and {method} to score — {score}.',
          '{minute}, {team} won it high up the pitch and moved quickly. {assister} squared it to {scorer}, who {method} home.',
          '{minute}, {assister} swung the set-piece into danger. {scorer} rose above the crowd and {method}, {team} {state}.',
          '{minute}, {scorer} met {assister}\'s cut-back and {method} first time — the attack made it {score}.',
          '{minute}, {assister} drew defenders before laying it off. {scorer} found space and {method}, {team} {state}.',
          '{minute}, {team} patiently moved the ball until the gap appeared. {assister} played the final pass and {scorer} {method} home.',
        ],
        solo: [
          '{minute}, {scorer} shook off a defender on the edge of the box and {method}, {team} {state} — {score}.',
          '{minute}, {scorer} pounced on a loose clearance and {method} into an empty net.',
          '{minute}, {scorer} drove from midfield to the arc and unleashed a {method} into the net.',
          '{minute}, {team}\'s high pressure caused chaos. {scorer} was quickest to react, {method} home to make it {score}.',
          '{minute}, {scorer} reacted first in the scramble, {method} from close range, {team} {state}.',
        ],
        methodHigh: ['header', 'tapped in', 'dinked', 'turned in from close range'],
        methodLow: ['long-range strike', 'curled finish', 'shot from distance', 'volley'],
        methodMid: ['struck', 'slotted', 'fired home', 'calm finish', 'angled shot'],
      },
      chance: [
        '{minute}, {team} almost {chanceState}. {player} was through on goal but the keeper came out on top.',
        '{minute}, {player} had a golden chance inside the box but put it wide at the last moment.',
        '{minute}, {team} broke quickly on the counter, but {player}\'s effort flew just past the post.',
        '{minute}, {player} met the cross with a good run but couldn\'t direct it on target.',
      ],
      redCard: [
        '{minute}, {team} suffered a blow as {player} was shown a red card, shifting the momentum of the match.',
        '{minute}, {player} was sent off, forcing {team} to tighten up for the remainder.',
        '{minute}, the referee showed red to {player}. {team}\'s game plan had to change on the spot.',
      ],
      flow: {
        xgGap: '{dominant} created the better chances, leading {xgHome}-{xgAway} in xG and {shotsHome}-{shotsAway} in shots.',
        possession: '{controller} dominated the ball with {possession}% possession, but efficiency in the box mattered more.',
        balanced: 'The quality gap in chances was narrow ({xgHome}-{xgAway} xG), with the match decided in transition and final execution.',
      },
      tactic: {
        control: [
          'Tactically, {controller} dictated the tempo, keeping the ball moving with patient passing and {possession}% possession.',
          '{controller}\'s plan was clear: hold the ball, probe through the wings and half-spaces, and prevent {away} from building rhythm.',
          '{controller} used sustained possession to weaken the opponent\'s press, keeping the match on their terms.',
          '{controller} stayed compact and patient in possession, turning ball retention into higher time spent in the final third.',
        ],
        direct: [
          '{direct} didn\'t chase possession — they prioritized getting the ball into the box quickly, and more shots reflected that approach.',
          'In attack, {creator} were sharper at finding dangerous areas, and their key-pass advantage kept the defense under pressure.',
          '{direct} moved the ball with purpose, bypassing midfield congestion and taking the game straight to goal.',
          '{creator} showed greater variety in the final third — crosses, through-balls, and second-ball challenges all created real threats.',
        ],
        pressure: [
          '{presser} put in a huge shift off the ball, making it hard for the opposition to play their first pass cleanly.',
          '{presser}\'s defensive discipline stood out — midfielders tracked back quickly and snuffed out danger before it reached the box.',
          'The physical intensity was high. {presser} cut off supply lines with relentless pressing and recovery runs.',
          '{presser} were aggressive in winning the ball back after losing it — not always leading to shots, but stifling counter-attacks.',
        ],
        balanced: [
          'Both sides were cautious in execution, keeping compact shapes and leaving the game to be decided in duels and second balls.',
          'This wasn\'t an open game. Neither side abandoned their defensive structure, relying on a few key passes to break through.',
          'Both teams managed risk carefully, limiting forward commitment. The game was shaped by a handful of high-quality moments.',
          'Tactically, the sides cancelled each other out: one tried to accelerate, the other clogged central spaces.',
          'Midfield battles repeatedly interrupted the rhythm, showing both sides prioritized defensive transitions.',
        ],
      },
      coach: {
        late: [
          'The coaching staff\'s patience paid off. {winner} didn\'t rush in the second half, waiting for the opponent to tire before landing the decisive blow.',
          '{winner}\'s substitutions and structure stayed synchronized, allowing sustained pressure late on — a sign of good game management.',
          '{winner} kept their shape in the stalemate. The late goal was a natural result of sustained pressure rather than desperation.',
          '{winner}\'s staff chose to keep pushing forward in the final phase, a decision that won them the decisive moment.',
        ],
        red: [
          'After the red card, {redTeam}\'s staff adjusted the shape, relying on a compact block and quick outlets to stay in the game.',
          '{opponent} tried to stretch play with the extra man, but {redTeam}\'s compact response kept the game from slipping away.',
          'Playing a man down tested {redTeam}\'s adaptability. They reduced risky passes through midfield and prioritized defensive solidity.',
          '{opponent}\'s staff urged quicker ball movement after the red, attempting to turn numerical advantage into wide overloads.',
        ],
        narrow: [
          '{winner}\'s staff made a pragmatic call — after taking the lead, they didn\'t chase more goals, but protected their advantage with solid positioning.',
          '{winner} prioritized control after scoring, cutting down on unnecessary risks and keeping the game manageable.',
          '{winner} won without extravagance. The staff\'s risk management was spot-on, giving {loser} few chances to fight back.',
          'The victory reflected {winner}\'s realistic approach: secure the defense first, then let the front players decide the game.',
        ],
        win: [
          '{winner} executed their game plan effectively, maintaining attacking structure even after taking the lead.',
          '{winner} positioned themselves intelligently in transitions. The staff clearly demanded immediate pressure on the ball after losing it.',
          '{winner}\'s win wasn\'t just about individual quality — their shape and second-ball protection underpinned the scoreline.',
          '{winner} showed mature game management, knowing when to push and when to keep the ball.',
        ],
        draw: [
          'Neither coaching staff took full risks in the draw. Late substitutions and positioning leaned toward avoiding defeat.',
          'Both sides tried to create through width, but the central defense was never consistently breached.',
          'The draw reflected both coaches\' caution: attacking intent was there, but neither would commit fully forward.',
          'Both teams strengthened midfield protection in the closing stages, more concerned about a counter-attack deciding the game.',
        ],
      },
      player: {
        goals: [
          '{player} was the standout name, changing the game with multiple finishes and earning a {rating} rating.',
          '{player}\'s finishing instinct decided the match — their runs consistently found the hardest spaces to defend.',
          '{team}\'s attack was finished off by {player}, whose calm shooting kept the opposition defense under constant pressure.',
          '{player} was the difference-maker, turning chances into real scoreboard pressure.',
        ],
        attack: [
          '{player} was influential in key moments. Beyond goals and assists, their link-up play kept {team} moving forward.',
          '{player}\'s contribution went beyond the stat sheet — they kept appearing where the attack needed a fulcrum.',
          '{player} seized their window in this match, making the key decisions that gave {team}\'s attack a focal point.',
          '{player} was decisive in the final third — knowing when to pass and when to shoot, a reliable outlet for {team}.',
          '{player}\'s movement created space, and their direct involvement shifted the match\'s momentum.',
        ],
        saves: [
          '{player}\'s work in goal was vital. Multiple saves kept {team} in the contest.',
          'Keeper {player} deserves a special mention, making save after save to deny the opposition.',
          '{team} held on under pressure thanks to {player}\'s reflexes — his shot-stopping was a key factor.',
          '{player} had little room for error, but handled everything cleanly, staying calm even against close-range efforts.',
        ],
        create: [
          '{player} was {team}\'s most important link in the final third, creating shooting opportunities for teammates with key passes.',
          '{player}\'s vision shaped {team}\'s attacking patterns — they consistently found passing lanes in congested areas.',
          '{team} needed someone to deliver the ball into dangerous zones, and {player} filled that role perfectly.',
          '{player} didn\'t just hunt for goals — they used passing to put teammates in better shooting positions.',
        ],
        defense: [
          '{player} covered huge ground defensively, with tackles and interceptions that solidified {team}\'s midfield shield.',
          '{team} limited the opponent\'s progress thanks to {player}\'s tracking back and anticipation.',
          '{player}\'s value came off the ball, repeatedly breaking up opposition passing moves before they developed.',
          '{player} deserves recognition for their defensive work, saving {team} from several dangerous situations around the box.',
        ],
        shots: [
          '{player} didn\'t convert their chances into goals, but constant shooting kept the opposition defense on edge.',
          'A large share of {team}\'s attacking threat came from {player}, who kept trying to break through with shots.',
          '{player}\'s aggression was notable — multiple shots showed they consistently found space in the final third.',
          '{player} wasn\'t far from a goal — their efforts already forced the opposing keeper into high alert.',
        ],
        general: [
          '{player} earned one of the highest ratings, providing steady play at both ends of the pitch for {team}.',
          '{player}\'s numbers don\'t tell the whole story — their involvement was crucial to keeping {team} in rhythm.',
          '{player}\'s all-round performance was the most recognized, making the right call in multiple situations.',
          '{player}\'s value lay in reliability — rarely wasting possession and always available for defensive work.',
        ],
      },
      impact: {
        finalWin: '{winner} won the final and lifted the World Cup trophy. {loser}\'s journey ended one step short.',
        thirdPlace: '{winner} finished third in the World Cup, while {loser} took fourth place.',
        semiFinal: '{winner} advanced to the final with this win, ending {loser}\'s hopes of lifting the trophy.',
        quarterFinal: '{winner} reached the semi-finals, now just one win away from the final.',
        knockoutWin: '{winner} progresses through the knockout stage, while {loser} is eliminated.',
        placementBothDark: 'Both teams far exceeded their pre-tournament odds — the third-place match was itself an underdog story.',
        placementThirdDark: '{thirdName} finished on the podium from outside the top 20 in pre-tournament odds, exceeding all expectations.',
        placementFourthDark: '{fourthName} lost this match but reaching the semi-finals already surpassed pre-tournament expectations.',
        placementBothFavored: 'Both teams are among the pre-tournament top 20 by odds, and reaching the final four largely matched expectations.',
        groupLead: '{teamName} have {points} points from {played} group matches, sitting {rank} in the group and in a strong position to qualify.',
        groupEliminated: '{teamName} have only {points} points from {played} matches, sitting {rank} in the group and unable to advance.',
        groupUpdate: 'After this match, {teamName} have {points} points, currently {rank} in Group {group}.',
        groupQualifiers: 'Group {groupId}: {names}',
        qualified: 'qualified',
        eliminated: 'eliminated',
        favoriteStatus: '{name} {status}',
        darkHorses: '{names} are the most notable dark horses of this stage, outperforming expectations in points, goal difference, or key matches.',
        noDarkHorses: 'Overall the favorites held their ground — group standings didn\'t see major upheaval.',
        unbeaten: '{names} remain unbeaten, showing impressive consistency.',
        scorers: 'Individually, {scorerLine}. {assisterLine}.{ratingLine}',
        scorerLead: '{name} leads the scoring chart with {goals} goals',
        scorerDisperse: 'The scoring race remains open',
        assisterLead: '{name} leads the assists chart with {assists}',
        assisterDisperse: 'The assists table is still tightly contested',
        ratingHighlight: ' {name} averages {rating} — one of the most outstanding players of the group stage.',
      },
      recap: {
        groupIntro: [
          'The group stage is complete. 32 teams have advanced and the World Cup moves into knockout mode.',
          '72 group matches are in the books. The qualifiers, surprises, and individual leaderboards have all taken shape.',
          'Group stage finished. Top teams\' consistency, underdog breakthroughs, and the scoring race defined this phase.',
        ],
        knockoutIntro: 'The {stage} is complete. {count} teams remain in the championship race.',
        knockoutWinners: '{names} booked their tickets to the next round.',
        knockoutTight: '{count} matches were decided by a single goal or went to penalties — knockout tension is rising.',
        knockoutClear: 'The outcomes in this round were relatively clear-cut, with the winners showing greater control.',
        knockoutBiggest: 'The most open match of the round was {match}, with {goals} total goals.',
        knockoutUpset: 'The upset came as {winner} beat {loser} — the lower-ranked side produced the more efficient performance.',
        knockoutScorers: 'Individually, {scorerLine}; {ratingLine}',
        knockoutScorerLead: '{name} still leads the scoring chart with {goals} goals',
        knockoutScorerDisperse: 'The scoring race is still wide open',
        knockoutRating: '{name} maintains a high rating, one of the most consistent stars of the knockout stage.',
        knockoutRatingGeneric: 'Key players\' form is beginning to shape the title race.',
        groupLines: 'Qualifiers by group: {lines}. {favorites}',
        groupFavorites: 'Among favorites, {status}.',
        groupQualifierFormat: 'Group {groupId}: {names}',
        groupMeta: '{count} qualified · {scorerInfo}',
        scorerInfo: 'Scorer {name}',
        scorerInfoUpdate: 'Stats updated',
        factQualified: '{count} qualified',
        factDarkHorses: 'Dark horses {names}',
        factStable: 'Favorites stable',
        factTopScorer: 'Top scorer {name} {goals}G',
        factTopAssister: 'Top assist {name} {assists}A',
        knockoutMeta: '{count} advanced · {tight} tight',
        factKnockoutTight: '{count} close matches',
        factKnockoutClear: 'Clear outcomes',
        factUpset: 'Upset {winner}',
        factOpen: '{goals} goals',
      },
      preview: {
        intro: 'The {stage} pairings are set. The next phase will narrow the field across {count} matches.',
        keyMatch: 'Key matchups to watch: {matches}.',
        noKeyMatch: 'No direct clashes between top-20 odds teams in this stage — favorites must avoid slips in seemingly favorable ties.',
        favorites: 'Based on form and squad strength, {names} are favored to advance.',
        noFavorites: 'The gap in this round is narrow — form and finishing may matter more than reputation.',
        tail: [
          'In the knockout stage, game management, set-piece defense, and goalkeeper form become critical. One mistake can reshape the entire bracket.',
          'This phase isn\'t just about attacking power. Substitution timing, risk management, and penalty preparation will influence outcomes.',
          'Favorites must avoid slow starts while underdogs look to drag the game into their comfort zone. The first 30 minutes will set the tone.',
        ],
        previewMeta: '{count} matches',
        factMatchup: '{count} ties',
        factFocus: 'Focus {matchup}',
        factFavorite: 'Favored {name}',
      },
      champion: {
        road: '{champion}\'s path to glory is complete: {count} matches, {wins} wins, {draws} draws, {goalsFor} goals scored, {goalsAgainst} conceded.',
        groupPath: 'In the group stage, {champion} secured qualification early: {matches}.',
        knockoutPath: 'In the knockout rounds, they overcame {opponents}.',
        signatureMatch: '{match} was the defining performance of their campaign.',
        meta: '{wins}W{draws} · {goalsFor}-{goalsAgainst}',
        drawsText: '{draws}D',
        fact: 'Champion {champion}',
        factGoal: 'Goals {goalsFor}',
        factConcede: 'Conceded {goalsAgainst}',
        factSignature: 'Signature {match}',
        tail: [
          'This champion\'s defining trait was stability: controlling games when leading and finding new attacking outlets when trailing or stuck.',
          'Their title wasn\'t built on a single spark — it came from defensive solidity, consistent output from key players, and sharp management.',
          'What set the champion apart was never being broken by a single bad result, handling every knockout round with composure.',
        ],
      },
      awards: {
        intro: 'With the final over, the individual awards of the World Cup have been decided. {bestPlayer}',
        bestPlayer: '{name} earned Best Player with consistent high ratings and key performances.',
        noBestPlayer: 'The Best Player race was extremely tight, with multiple stars producing world-class displays.',
        topScorer: 'The Golden Boot goes to {name} with {goals} goals — the tournament\'s most reliable finisher.',
        noTopScorer: 'The scoring chart had no runaway leader, but forward efficiency still decided many matches.',
        topAssister: 'In assists, {name} led with {assists} — their passing directly changed multiple matches.',
        bestKeeper: 'Best Goalkeeper goes to {name}, with {saves} saves and an average of {conceded} goals conceded per match.',
        noBestKeeper: 'The goalkeeper race was concentrated among a few steady knockout-stage performers.',
        scorerFallback: 'the scoring leader',
        keeperFallback: 'a top keeper candidate',
        summary: '{names} formed the individual narrative of this tournament — one created, one finished, and one held the line.',
        noSummary: 'Individual performances were strongly tied to team systems, shaped by tactical frameworks as much as personal quality.',
      },
      review: {
        intro: 'The tournament is over. {total} matches produced {goals} goals, an average of {average} per game.',
        scoring: '{highScoring} matches had 4+ goals, and {btts} saw both teams score. {biggest}',
        biggest: 'The most open game was {match}.',
        upsetSep: 'beat',
        assisterFallback: 'multiple creators',
        ratingFallback: 'key players',
        upsets: 'Upset memories include {upsets}.',
        noUpsets: 'For the most part, favorites delivered on their paper advantage, though knockout ties were often shaped by fine margins.',
        champion: 'In the end, {champion} held their nerve longest, becoming the defining story of the tournament.',
        leaders: 'Individually, {scorer}\'s finishing, {creators}\' creativity, and {ratings}\' high ratings formed the statistical backbone of the tournament.',
        noLeaders: 'This tournament had both dominance and surprise — the actual stories were richer than any single stat line.',
        awardsMeta: 'Best Player {name}',
        awardsMetaDefault: 'Awards overview',
        factBestPlayer: 'Best Player {name}',
        factTopScorer: 'Top scorer {name} {goals}G',
        factBestKeeper: 'Best GK {name}',
        factTopAssister: 'Assists leader {name} {assists}A',
        reviewMeta: '{count} games · {goals} goals',
        factTotalGoals: 'Total goals {goals}',
        factAvgGoals: 'Avg {average} per game',
        factHighScoring: '{count} with 4+ goals',
        factBothScored: '{count} both scored',
      },
    },
  },
  helpers: {
    formText: {
      W: 'W',
      D: 'D',
      L: 'L',
    },
    goalState: {
      equalize: 'level the score',
      lead: 'take the lead',
      extend: 'extend the lead',
      chase: 'pull one back',
    },
    chanceState: {
      extend: 'extend the lead',
      break: 'break the deadlock',
      face: 'save face',
      close: 'close the gap',
    },
    tacticSummary: 'Mentality {mentality} · Focus {focus} · Pressing {pressing} · Line {line}',
    teamMate: 'a teammate',
    attacker: 'an attacker',
    defender: 'a defender',
    advance: ' advanced',
    groupLeader: 'Group {group} leaders {teamName}, {points} pts',
    unknownPlayer: 'Unknown player',
    unsolved: 'N/A',
    minute: '{minute}\'{stoppage}',
    stoppageTime: '+{minute}',
  },
  rendering: {
    playerTotals: {
      headers: ['Player', 'Pos', 'App', 'Min', 'G', 'A', 'xG', 'S', 'SoT', 'Pas', 'Cmp', 'KP', 'Tkl', 'Int', 'YC', 'RC', 'Sv', 'Rt'],
    },
    matchStats: {
      possession: 'Possession',
      shotsOnTarget: 'Shots on Target',
      passes: 'Passes',
      passSuccess: 'Pass %',
      keyPasses: 'Key Passes',
      corners: 'Corners',
      offsides: 'Offsides',
      fouls: 'Fouls',
      yellowCards: 'Yellow Cards',
      redCards: 'Red Cards',
      saves: 'Saves',
      tackles: 'Tackles',
      interceptions: 'Interceptions',
    },
    modal: {
      best: 'Best',
      record: 'Record',
      stage: 'Stage',
      bestPlayer: 'Best Player',
      signature: 'Signature Match',
    },
    bracket: {
      pending: 'TBD',
    },
  },
  mobile: {
    help: {
      title: 'Help',
      intro: 'This is the 2026 World Cup Simulator. From the opening match to the final, tap Continue to watch the entire tournament unfold.',
      howToPlay: 'Press the Continue button to advance matches, read news to follow tournament progress, and browse fixtures, standings, knockout rounds, and stats anytime.',
      pcMore: 'Visit on PC for Coach Mode — take charge of a national team, adjust tactics, and edit lineups for a more complete experience.',
      follow: 'Follow us',
    },
    landing: {
      intro: '2026 World Cup Simulator',
      description: 'From the opening match to the final night, tap Continue to watch the whole 2026 World Cup simulation unfold.',
      welcome: 'Welcome to your own World Cup timeline. Tap through a fast simulation and see who lifts the trophy. Visit on PC for Coach Mode and more features.',
      hiddenStart: 'Sim with hidden teams',
      start: 'Start simulation',
      startTitle: 'Start simulation now',
      startSubtitle: 'Real teams, real groups, and a complete spectator-view simulation of every match in under ten minutes.',
      hiddenCheckbox: 'Let Italy, Poland, Indonesia, China, and UAE join the World Cup',
    },
    topbar: {
      settings: 'Settings',
    },
    tabs: {
      navigation: 'Mobile navigation',
      news: 'News',
      fixtures: 'Fixtures',
      standings: 'Table',
      knockout: 'Knockout',
      data: 'Data',
    },
    action: {
      continue: 'Continue',
      readNews: 'Read news',
      simulateNext: 'Sim next matchday',
      restart: 'Restart',
      completed: 'Completed',
    },
    settings: {
      title: 'Settings',
      language: 'Language',
      restart: 'Restart',
      restartConfirm: 'Restart and clear the current mobile simulation?',
      close: 'Close settings',
    },
    news: {
      previous: 'Previous',
      next: 'Next',
      pcMore: 'Use the PC version for more surprises and features.',
      empty: 'No news yet.',
    },
    fixtures: {
      previousDay: 'Previous matchday',
      nextDay: 'Next matchday',
      noMatches: 'No fixtures on this matchday.',
    },
    standings: {
      groupStandings: 'Group standings',
      knockout: 'Knockout',
      roundOf32: 'Round of 32',
      roundOf16: 'Round of 16',
      quarterFinal: 'Quarter-finals',
      semiFinal: 'Semi-finals',
      thirdPlace: 'Third place',
      final: 'Final',
    },
    data: {
      goals: 'Goals',
      assists: 'Assists',
      pcMore: 'Use the PC version to view more player and team data.',
      empty: 'No data yet.',
    },
    team: {
      squad: 'Squad',
      fixtures: 'Fixtures',
      tactic: 'Tactic',
      close: 'Close team overlay',
    },
  },
    teamNames: {

    mexico: 'Mexico', south_africa: 'South Africa', south_korea: 'South Korea',
    czechia: 'Czechia', canada: 'Canada', bosnia_herzegovina: 'Bosnia & Herzegovina',
    switzerland: 'Switzerland', qatar: 'Qatar', brazil: 'Brazil', morocco: 'Morocco',
    scotland: 'Scotland', haiti: 'Haiti', united_states: 'United States',
    australia: 'Australia', paraguay: 'Paraguay', turkiye: 'Türkiye',
    germany: 'Germany', curacao: 'Curaçao', ivory_coast: 'Ivory Coast',
    ecuador: 'Ecuador', netherlands: 'Netherlands', japan: 'Japan',
    tunisia: 'Tunisia', sweden: 'Sweden', belgium: 'Belgium', iran: 'Iran',
    egypt: 'Egypt', new_zealand: 'New Zealand', spain: 'Spain',
    uruguay: 'Uruguay', saudi_arabia: 'Saudi Arabia', cape_verde: 'Cape Verde',
    france: 'France', senegal: 'Senegal', norway: 'Norway', iraq: 'Iraq',
    argentina: 'Argentina', austria: 'Austria', algeria: 'Algeria',
    jordan: 'Jordan', portugal: 'Portugal', colombia: 'Colombia',
    uzbekistan: 'Uzbekistan', dr_congo: 'DR Congo', england: 'England',
    croatia: 'Croatia', panama: 'Panama', ghana: 'Ghana',
    italy: 'Italy',
    tbd: 'TBD',
    china: 'China',

    indonesia: "Indonesia",
    united_arab_emirates: "UAE",
    poland: "Poland",

  },
};
